import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageSquare, Pencil, Plus, Send, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import { Field, PageHeader } from '@/components/ui/form-field';
import { Alert, Badge, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Switch } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import { formatDateTime } from '@/lib/utils';

interface Channel {
  id: string;
  name: string;
  active: boolean;
  webhookHost: string;
  lastError: string | null;
  lastSentAt: string | null;
}
const onError = (e: unknown) => toast.error(errorMessage(e));

export function TeamsPage() {
  const qc = useQueryClient();
  const [confirmDialog, confirm] = useConfirm();
  const [edit, setEdit] = useState<{ id: string | null; name: string; webhookUrl: string; active: boolean } | null>(null);
  const { data } = useQuery({ queryKey: ['teams-channels'], queryFn: () => api<{ items: Channel[] }>('/integrations/teams') });
  const refresh = () => qc.invalidateQueries({ queryKey: ['teams-channels'] });
  const save = useMutation({
    mutationFn: () => {
      const body = { name: edit!.name, active: edit!.active, ...(edit!.webhookUrl ? { webhookUrl: edit!.webhookUrl } : {}) };
      return edit!.id ? api(`/integrations/teams/${edit!.id}`, { method: 'PUT', body }) : api('/integrations/teams', { method: 'POST', body });
    },
    onSuccess: () => {
      setEdit(null);
      refresh();
      toast.success('Kanal gespeichert');
    },
    onError,
  });
  const test = useMutation({
    mutationFn: (id: string) => api<{ ok: boolean; message: string }>(`/integrations/teams/${id}/test`, { method: 'POST' }),
    onSuccess: (r) => {
      (r.ok ? toast.success : toast.error)(r.message);
      refresh();
    },
    onError,
  });
  const remove = useMutation({ mutationFn: (id: string) => api(`/integrations/teams/${id}`, { method: 'DELETE' }), onSuccess: refresh, onError });

  return (
    <div className="grid gap-4">
      {confirmDialog}
      <PageHeader
        title="Microsoft Teams"
        description="Interne Benachrichtigungen in Teams-Kanäle – welche Ereignisse wohin gehen, legen Sie unter Automatisierungen fest (Aktion „Teams-Nachricht“)."
        actions={
          <Button onClick={() => setEdit({ id: null, name: '', webhookUrl: '', active: true })}>
            <Plus /> Kanal
          </Button>
        }
      />
      <Card>
        <CardHeader>
          <CardTitle className="text-base">So richten Sie einen Kanal ein</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-2 text-sm">
          <ol className="list-decimal space-y-1 pl-5">
            <li>
              In Teams beim gewünschten Kanal auf <strong>„…“ → Workflows</strong> klicken und die Vorlage <strong>„Bei Empfang einer Webhookanforderung in einem Kanal posten“</strong> wählen.
            </li>
            <li>Workflow benennen, Team und Kanal bestätigen und speichern.</li>
            <li>Die angezeigte Adresse (HTTP-URL) kopieren und hier als Webhook-Adresse eintragen.</li>
          </ol>
          <p className="text-xs text-muted-foreground">Die Adresse wird verschlüsselt gespeichert und nicht wieder angezeigt. Erlaubt sind nur Adressen von Microsoft (Power Automate/Workflows).</p>
        </CardContent>
      </Card>
      {edit && (
        <Card>
          <CardContent className="pt-5">
            <form
              className="grid gap-3 sm:grid-cols-[14rem_1fr_auto_auto] sm:items-end"
              onSubmit={(e) => {
                e.preventDefault();
                save.mutate();
              }}
            >
              <Field label="Name" htmlFor="tc-name">
                <Input id="tc-name" required value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="z. B. Technik" />
              </Field>
              <Field label={edit.id ? 'Webhook-Adresse (leer = unverändert)' : 'Webhook-Adresse'} htmlFor="tc-url">
                <Input id="tc-url" required={!edit.id} value={edit.webhookUrl} onChange={(e) => setEdit({ ...edit, webhookUrl: e.target.value })} placeholder="https://…logic.azure.com/workflows/…" autoComplete="off" />
              </Field>
              <label className="flex h-9 items-center gap-2 text-sm">
                <Switch checked={edit.active} onCheckedChange={(v) => setEdit({ ...edit, active: v })} /> Aktiv
              </label>
              <div className="flex gap-2">
                <Button type="submit" loading={save.isPending}>
                  Speichern
                </Button>
                <Button type="button" variant="ghost" onClick={() => setEdit(null)}>
                  Abbrechen
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
      <div className="grid gap-3 lg:grid-cols-2">
        {data?.items.length === 0 && !edit && <p className="text-sm text-muted-foreground">Noch kein Kanal eingerichtet.</p>}
        {data?.items.map((c) => (
          <Card key={c.id}>
            <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
              <div className="min-w-0">
                <CardTitle className="flex items-center gap-2 text-base">
                  <MessageSquare className="size-4 text-muted-foreground" /> {c.name}
                  {!c.active && <Badge variant="outline">aus</Badge>}
                  {c.lastError ? <Badge variant="destructive">Fehler</Badge> : c.lastSentAt ? <Badge variant="success">ok</Badge> : null}
                </CardTitle>
                <CardDescription>
                  {c.webhookHost} · zuletzt gesendet: {formatDateTime(c.lastSentAt)}
                </CardDescription>
                {c.lastError && <Alert variant="destructive" className="mt-2 text-xs">{c.lastError}</Alert>}
              </div>
              <div className="flex shrink-0 gap-1">
                <Button variant="outline" size="sm" onClick={() => test.mutate(c.id)} loading={test.isPending && test.variables === c.id}>
                  <Send /> Test
                </Button>
                <Button variant="ghost" size="icon" className="size-8" aria-label={`${c.name} bearbeiten`} onClick={() => setEdit({ id: c.id, name: c.name, webhookUrl: '', active: c.active })}>
                  <Pencil className="size-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 text-destructive hover:text-destructive"
                  aria-label={`${c.name} löschen`}
                  onClick={async () => (await confirm({ title: `Kanal „${c.name}“ entfernen?`, description: 'Automatisierungen mit diesem Kanal senden danach nicht mehr.', confirmLabel: 'Entfernen', destructive: true })) && remove.mutate(c.id)}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            </CardHeader>
          </Card>
        ))}
      </div>
    </div>
  );
}
